import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import dayjs from 'dayjs';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import ModalComponent from 'components/Common/Modal';
import ContentRelated from 'components/ContentRelated';
import useCallRelatedStakeholders from 'components/Dashboard/KeyUpdates/useCallStakeholders';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import { store } from 'components/Store';
import useEmailComponentFunctions from 'components/Dashboard/Influence/CrmEmails/useEmailComponentFunctions';
import InteractionFormFields, {
  InteractionFormFieldsSkeleton,
  DEFAULT_INTERACTION_TITLE,
} from './InteractionFormFields';
import { useInteractionsList } from './InteractionsListContext';
import normalizeInteractionHtmlBody from './normalizeInteractionHtmlBody';
import { stripEmailAttachmentControls } from 'components/Dashboard/Influence/CrmEmails/buildEmailAttachmentHtml';

const CrmInteractionDetail = () => {
  const { interactionId } = useParams();
  const { getEmail } = useEmailComponentFunctions();
  const [recipientsSendRecords, setRecipientsSendRecords] = useState(null);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
  const [senderEmailAddress, setSenderEmailAddress] = useState(null);
  const [emailSubject, setEmailSubject] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingLeaveUrl, setPendingLeaveUrl] = useState(null);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: DEFAULT_INTERACTION_TITLE,
    date: '',
    type: '',
    description: '',
    tags: [],
    initiator: '',
    stakeholders: [],
    members: [],
  });

  const initialFormRef = useRef(null);
  const [isBaselineReady, setIsBaselineReady] = useState(false);
  const baselineTimeoutRef = useRef(null);
  const hasUserInteractedRef = useRef(false);
  // TinyMCE can re-serialize the loaded description slightly differently than the raw API value
  // (e.g. adding rel="noopener" to a link) purely on its own init, with no user interaction - if the
  // baseline gets captured before that settles, isDirty flashes true then self-corrects a moment
  // later. Waiting for the editor's own onInit before arming the baseline timer avoids that race.
  const [isEditorReady, setIsEditorReady] = useState(false);

  const { state, dispatch } = useContext(store);
  const { updateInteractionInCache } = useInteractionsList();
  const { generalApiCall } = useGeneralApiCall();
  const [heightContainer, containerRef] = useHeightContainer();
  // useHeightContainer's containerRef is a callback ref (a function, no .current) - useOutsideElement
  // needs a real ref object, so this tracks the same DOM node separately for that.
  const pageContentRef = useRef(null);
  const { contentResults, setContentResults } = useContentResults();
  const [isLoadingRelatedContent, setIsLoadingRelatedContent] = useState(true);
  const { callRelatedStakeholders } = useCallRelatedStakeholders();
  const numberOfStakeHoldersToShow = parseInt(import.meta.env.VITE_APP_RELATED_STAKEHOLDERS_TO_SHOW);
  const crmSection = contentResults['CrmInteractions'];

  const loadInteraction = useRef(null);
  loadInteraction.current = async (source) => {
    try {
      setIsLoading(true);
      setIsLoadingRelatedContent(true);
      const result = await generalApiCall({
        method: 'get',
        pathname: `/api/crm-interaction/${interactionId}`,
        needsAuthentication: true,
        requestSource: source,
        notShowErrorMessage: true,
      });
      if (result) {
        const loadedForm = {
          title: result.title ?? DEFAULT_INTERACTION_TITLE,
          date: result.dateTime ? dayjs(result.dateTime).format('YYYY-MM-DD') : '',
          type: result.interactionType ?? '',
          description: normalizeInteractionHtmlBody(result),
          tags: (result.tags ?? []).map((tag) => ({ label: tag, value: tag })),
          initiator:
            result.initiator ??
            (result.initiatedByOrganisation === true
              ? 'Team'
              : result.initiatedByOrganisation === false
                ? 'Stakeholder'
                : ''),
          stakeholders: (result.stakeholderContacts ?? []).map((c) => ({
            value: c.id,
            label: c.fullName ?? '',
            id: c.id,
            email: '',
            imageId: c.imageId ?? null,
            imageUrl: null,
            stakeholderContactId: c.stakeholderContactId ?? null,
            party: c.party ?? null,
          })),
          members: (result.associatedUserIds ?? []).map((id, idx) => {
            const nameFromResponse = result.associatedUserFullNames?.[idx];
            const member = !nameFromResponse ? state.team?.allMembers?.find((m) => m.id === id) : null;
            return { value: id, label: nameFromResponse ?? member?.fullName ?? member?.emailAddress ?? `User ${id}` };
          }),
          sourceCrmEmailId: result.sourceCrmEmailId ?? null,
        };
        setForm(loadedForm);
        initialFormRef.current = loadedForm;
        hasUserInteractedRef.current = false;
        setIsLoading(false);
        setIsBaselineReady(false);
        setIsEditorReady(false);
        dispatch({
          type: 'MODIFY_SECTION',
          parameter: 'interactionDetail',
          value: {
            title: loadedForm.title,
            sourceCrmEmailId: result.sourceCrmEmailId ?? null,
            interactionType: result.interactionType,
            dateTime: result.dateTime,
          },
        });
        await loadRelatedContent(result, source);
        setIsLoading(false);
        setIsLoadingRelatedContent(false);
      }
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    loadInteraction.current(source);
    return () => {
      source.abort('Interaction detail cancelled');
      dispatch({ type: 'MODIFY_SECTION', parameter: 'interactionDetail', value: null });
      clearTimeout(baselineTimeoutRef.current);
    };
  }, [interactionId]);

  useEffect(() => {
    if (isLoading || !isEditorReady) return;
    if (isBaselineReady && hasUserInteractedRef.current) return;
    clearTimeout(baselineTimeoutRef.current);
    baselineTimeoutRef.current = setTimeout(() => {
      initialFormRef.current = form;
      setIsBaselineReady(true);
    }, 400);
    return () => clearTimeout(baselineTimeoutRef.current);
  }, [form, isLoading, isBaselineReady, isEditorReady]);

  // CrmInteraction is private, team-scoped CRM data - the more-like-this "related content" search
  // isn't a supported type for it backend-side (and conceptually doesn't apply to private records
  // the way it does for public news content), so this only loads the interaction's own stakeholders.
  const loadRelatedContent = async (interaction, source) => {
    const stakeholderIds = interaction.contactIds ?? [];

    let relatedContentWithStakeholders = { hits: {} };
    let stakeholdersRendered = [];
    let contactsIdsCalled = [];

    if (stakeholderIds.length > 0) {
      const initialIds = stakeholderIds.slice(0, numberOfStakeHoldersToShow);
      contactsIdsCalled = initialIds;
      const filters = initialIds.map((id) => ({ field: 'id', value: `${id}`, operator: 'str_eq' }));
      const stakeHolderResults = await callRelatedStakeholders({ stakeholderFilters: filters, source });
      stakeholdersRendered = stakeHolderResults?.data?.hits ?? [];
      relatedContentWithStakeholders = {
        ...relatedContentWithStakeholders,
        hits: { ...relatedContentWithStakeholders.hits, StakeHolders: stakeholdersRendered },
      };
    }

    setContentResults((prev) => ({
      ...prev,
      CrmInteractions: {
        ...prev.CrmInteractions,
        contentRelated: relatedContentWithStakeholders,
        relatedStakeholders:
          stakeholderIds.length > 0 ? stakeholderIds.filter((id) => !contactsIdsCalled.includes(id)) : null,
        relatedStakeholdersRendered: stakeholdersRendered.length,
      },
    }));
    setIsLoadingRelatedContent(false);
  };

  const handleChange = (field, value) => {
    // Not just the onPointerDownCapture/onKeyDownCapture below: tags and stakeholders/members pick
    // from a menu portaled to document.body (see menuPortalTarget in ContactsSelectShared.jsx and
    // AddTagsModal.jsx's customContact mode), so a click there never bubbles through this page's own
    // DOM subtree. Setting it here instead - on the one function every field change already goes
    // through - catches those the same as everything else.
    hasUserInteractedRef.current = true;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleCancel = () => {
    setForm(initialFormRef.current);
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'interactionDetail',
      value: {
        ...state.interactionDetail,
        title: initialFormRef.current.title,
        interactionType: initialFormRef.current.type,
        dateTime: initialFormRef.current.date,
      },
    });
  };

  const handleSave = async () => {
    if (!form.type) {
      createNotification('danger', 'Please select an interaction type');
      return false;
    }
    if (!form.date) {
      createNotification('danger', 'Please select a date');
      return false;
    }
    setIsSaving(true);
    const result = await generalApiCall({
      method: 'post',
      pathname: '/api/crm-interaction/update',
      requestProperties: {
        id: Number(interactionId),
        title: form.title,
        date: form.date,
        type: form.type,
        description: stripEmailAttachmentControls(form.description),
        tags: form.tags.map((tag) => tag.value),
        initiatedByOrganisation: form.initiator === 'Stakeholder' ? false : form.initiator ? true : null,
        initiator: form.initiator || null,
        contactIds: form.stakeholders.map((p) => p.id),
        userIds: form.members.map((m) => m.value),
      },
      needsAuthentication: true,
    });
    setIsSaving(false);
    if (result) {
      initialFormRef.current = form;
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'interactionDetail',
        value: { ...state.interactionDetail, title: form.title, interactionType: form.type, dateTime: form.date },
      });
      // The list page doesn't refetch on returning from this page (intentional - avoids collapsing
      // an infinite-scrolled list back to page 1) - it remounts and re-seeds from the cache instead,
      // so patch that cache directly here rather than reaching for the global store.
      updateInteractionInCache({
        id: Number(interactionId),
        dateTime: form.date,
        interactionType: form.type,
        description: stripEmailAttachmentControls(form.description),
        tags: form.tags.map((tag) => tag.value),
        crmContactIds: form.stakeholders.map((p) => p.id),
        crmContactNames: form.stakeholders.map((p) => p.label),
        crmContactImageIds: form.stakeholders.map((p) => p.imageId),
      });
      createNotification('success', 'Interaction saved');
    }
    return !!result;
  };

  const handleDeleteInteraction = async () => {
    setIsDeleting(true);
    // The delete endpoint responds 200 with an empty body, so `result` (response.data) would be
    // an empty, falsy string — request the full response and check the status instead.
    const response = await generalApiCall({
      method: 'post',
      pathname: '/api/crm-interaction/delete',
      requestProperties: [Number(interactionId)],
      needsAuthentication: true,
      returnCompleteRequest: true,
    });
    setIsDeleting(false);
    if (response?.status === 200) {
      createNotification('success', 'Interaction deleted successfully');
      navigate('/people/interactions');
    }
  };

  const normalizeHtml = (html) => {
    if (!html) return html;
    const doc = new DOMParser().parseFromString(stripEmailAttachmentControls(html), 'text/html');
    // TinyMCE's own serializer can silently add attributes like rel="noopener" to links when it
    // first renders loaded content, with zero user interaction - strip rel so that alone doesn't
    // falsely mark the form as dirty right after load.
    doc.querySelectorAll('a[rel]').forEach((a) => a.removeAttribute('rel'));
    return doc.body.innerHTML;
  };
  const isDirty =
    isBaselineReady &&
    !!initialFormRef.current &&
    (() => {
      const { description: prevDescription, ...prevRest } = initialFormRef.current;
      const { description: currentDescription, ...currentRest } = form;
      const restDiffers = JSON.stringify(prevRest) !== JSON.stringify(currentRest);
      const descriptionDiffers = normalizeHtml(prevDescription) !== normalizeHtml(currentDescription);
      return restDiffers || descriptionDiffers;
    })();
  const SAVE_BAR_HEIGHT = 68;

  // Catches clicks on in-app links (nav, sidebar, breadcrumbs) outside this page's own content
  // while there are unsaved changes, so navigating away doesn't silently lose them - same pattern
  // used for the CRM email composer (see useEmailComponentEffects.js).
  useOutsideElement(
    pageContentRef,
    (target, event) => {
      if (!isDirty) return;
      const anchorElement = target?.closest?.('a');
      const targetHref = anchorElement?.href;
      if (!targetHref) return;
      event?.preventDefault?.();
      event?.stopPropagation?.();
      setPendingLeaveUrl(targetHref.replace(window.location.origin, ''));
    },
    [isDirty],
    'click',
    true
  );

  const handleDiscardAndLeave = () => {
    const url = pendingLeaveUrl;
    setPendingLeaveUrl(null);
    handleCancel();
    navigate(url);
  };

  const handleSaveAndLeave = async () => {
    const url = pendingLeaveUrl;
    const saved = await handleSave();
    if (saved) {
      setPendingLeaveUrl(null);
      navigate(url);
    }
  };

  // Email campaigns replace People-from-org/Stakeholders with the real send records (for the same
  // Recipients component the email performance page uses) - fetch the source email for those, and
  // reuse its already-resolved sender address for the (locked) Sender field instead of a BE round-trip.
  useEffect(() => {
    if (!form.sourceCrmEmailId) return;
    const source = new AbortController();
    setIsLoadingRecipients(true);
    (async () => {
      const crmEmailModel = await getEmail({ emailId: form.sourceCrmEmailId, source });
      if (crmEmailModel && crmEmailModel.status !== 403 && crmEmailModel.status !== 404) {
        setRecipientsSendRecords(crmEmailModel.sendRecords ?? []);
        setSenderEmailAddress(crmEmailModel.senderEmailAddress ?? null);
        setEmailSubject(crmEmailModel.subject ?? null);
      }
      setIsLoadingRecipients(false);
    })();
    return () => source.abort('Interaction recipients fetch cancelled');
  }, [form.sourceCrmEmailId]);

  const contentRelatedData = {
    objectId: `CrmInteraction-${interactionId}`,
    objectType: 'CrmInteraction',
    type: 'CrmInteraction',
    id: Number(interactionId),
    title: form.title,
    contentUrl: `/people/interactions/${interactionId}`,
    sourceCrmEmailId: form.sourceCrmEmailId,
    interactionType: form.type,
    dateTime: form.date,
  };

  return (
    <div className='main-content px-md-4 px-3'>
      <div
        className='row px-0 piece-content-container'
        ref={(node) => {
          containerRef(node);
          pageContentRef.current = node;
        }}
      >
        <div className='general-content-column padding-to-nav p-0 z-100'>
          <div className='content-container h-100'>
            <CustomScrollbar
              className='simple-scrollbar indented-scrollbar-small'
              style={{ height: `${heightContainer - (isDirty ? SAVE_BAR_HEIGHT : 0)}px` }}
              maximalThumbYSize={100}
            >
              <div
                className='p-3 p-lg-4 pb-3'
                onPointerDownCapture={() => {
                  hasUserInteractedRef.current = true;
                }}
                onKeyDownCapture={() => {
                  hasUserInteractedRef.current = true;
                }}
              >
                {isLoading ? (
                  <InteractionFormFieldsSkeleton />
                ) : (
                  <>
                    <InteractionFormFields
                      form={form}
                      onChange={handleChange}
                      recipientsSendRecords={recipientsSendRecords}
                      isLoadingRecipients={isLoadingRecipients}
                      senderEmailAddress={senderEmailAddress}
                      emailSubject={emailSubject}
                      onEditorReady={() => setIsEditorReady(true)}
                    />
                    {!form.sourceCrmEmailId && (
                      <div className='d-flex justify-content-between align-items-center specific-top-margin-22'>
                        <button
                          className='general-button paragraph-p2 text-danger-background'
                          onClick={() => setShowDeleteModal(true)}
                        >
                          Delete interaction
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CustomScrollbar>
            {isDirty && (
              <div
                className='d-flex justify-content-start justify-content-lg-end align-items-center px-3 px-lg-4'
                style={{
                  height: `${SAVE_BAR_HEIGHT}px`,
                  position: 'relative',
                  zIndex: 10,
                  backgroundColor: 'rgba(0, 148, 204, 0.04)',
                  boxShadow: '0 -1px 3px rgba(60, 63, 66, 0.12), 0 -4px 10px rgba(60, 63, 66, 0.05)',
                }}
              >
                <button
                  className='general-button border rounded-pill py-2 px-4 paragraph-p2 mr-3'
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  className='general-button action-button rounded-pill py-2 px-4 paragraph-p2'
                  onClick={handleSave}
                  disabled={isSaving || !isDirty}
                >
                  {isSaving ? 'Updating...' : 'Update interaction'}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className='related-content-column show-lg px-0'>
          <ContentRelated
            data={contentRelatedData}
            content={crmSection?.contentRelated}
            relatedStakeholders={crmSection?.relatedStakeholders}
            relatedStakeholdersRendered={crmSection?.relatedStakeholdersRendered ?? 0}
            mainSection='CrmInteractions'
            isLoadingRelatedContent={isLoadingRelatedContent}
          />
        </div>
      </div>
      <ModalComponent maxWidth={480} isOpen={showDeleteModal} setIsOpen={setShowDeleteModal}>
        <div className='p-4 px-lg-5 pt-5 pb-lg-2'>
          <div className='text-center'>
            <p>Are you sure you want to delete this interaction?</p>
            <div className='my-4'>
              <button
                className='general-button outline-button general-button-spacing mr-2 mr-lg-3 rounded-pill'
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className={`general-button general-button-spacing delete-note-button ${isDeleting ? '' : 'text-white'} rounded-pill`}
                onClick={handleDeleteInteraction}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete interaction'}
              </button>
            </div>
          </div>
        </div>
      </ModalComponent>
      <ModalComponent
        maxWidth={500}
        isOpen={!!pendingLeaveUrl}
        setIsOpen={(open) => !open && setPendingLeaveUrl(null)}
      >
        <div className='p-3 px-lg-5 pt-lg-5 pb-lg-2'>
          <div className='text-center'>
            <p className='title-h5-m'>{"Do you want to discard the changes you've made?"}</p>
            <div className='my-4'>
              <button
                style={{ backgroundColor: '#9b171f' }}
                className='general-button action-button rounded-pill px-4 py-2 mr-3'
                onClick={handleDiscardAndLeave}
                disabled={isSaving}
              >
                Discard changes
              </button>
              <button
                disabled={isSaving}
                className='general-button action-button rounded-pill px-4 py-2'
                onClick={handleSaveAndLeave}
              >
                {isSaving && <i className='fas fa-spinner fa-spin'></i>} Update interaction
              </button>
            </div>
          </div>
        </div>
      </ModalComponent>
    </div>
  );
};

export default CrmInteractionDetail;
